
import { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import api from "../services/api";
import { toast } from "react-toastify";

import {
  FaChalkboardTeacher,
  FaPlus,
  FaTrash,
  FaEnvelope,
  FaPhone,
  FaBook,
  FaTimes,
  FaSearch,
  FaUserGraduate,
  FaSpinner,
  FaUserTie,
  FaCheckCircle,
  FaGraduationCap
} from "react-icons/fa";

// ==========================================
// ANIMAÇÕES
// ==========================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(25px) scale(.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ==========================================
// ESTILOS
// ==========================================
const Container = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 22px;
  color: #202633;
  overflow: visible;
  background: #f7f8fc;
  border-radius: 18px;

  * { box-sizing: border-box; }

  @media (max-width: 768px) {
    padding: 13px;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const HeaderIcon = styled.div`
  width: 58px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 17px;
  background: linear-gradient(145deg, #c70303, #7d0000);
  color: #fff;
  font-size: 25px;
  flex-shrink: 0;
  box-shadow: 0 9px 20px rgba(199, 3, 3, .20);
`;

const Heading = styled.div`
  h2 {
    margin: 0;
    font-size: 25px;
    font-weight: 850;
    letter-spacing: -.5px;
    color: #202633;
  }
  p {
    margin: 5px 0 0;
    color: #778094;
    font-size: 13px;
  }
  @media (max-width: 480px) {
    h2 { font-size: 21px; }
    p { font-size: 11px; }
  }
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: 12px;
  padding: 13px 19px;
  background: linear-gradient(135deg, #d20a0a, #a30000);
  color: #fff;
  font-weight: 750;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 7px 17px rgba(199, 3, 3, .18);
  transition: transform .2s, box-shadow .2s, background .2s;

  svg { font-size: 15px; }
  &:hover {
    background: linear-gradient(135deg, #b90000, #780000);
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(199, 3, 3, .24);
  }
  &:active { transform: translateY(0) scale(.98); }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 23px;
  @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 480px) { grid-template-columns: 1fr; gap: 10px; }
`;

const StatCard = styled.div`
  position: relative;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e9edf4;
  border-radius: 16px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 5px 18px rgba(31, 42, 68, .045);
  transition: transform .2s, box-shadow .2s;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 24px rgba(31, 42, 68, .08);
  }
`;

const StatIcon = styled.div`
  width: 49px;
  height: 49px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$bg || "#fff0f0"};
  color: ${p => p.$color || "#c70303"};
  font-size: 21px;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px ${p => p.$color || "#c70303"}14;
`;

const StatText = styled.div`
  min-width: 0;
  span {
    display: block;
    color: #7d8798;
    font-size: 11px;
    font-weight: 650;
    margin-bottom: 5px;
  }
  strong {
    display: block;
    font-size: 25px;
    line-height: 1;
    color: #202633;
    font-weight: 850;
  }
`;

const ContentCard = styled.div`
  width: 100%;
  min-width: 0;
  background: #fff;
  border: 1px solid #e8ecf3;
  border-radius: 17px;
  overflow: hidden;
  box-shadow: 0 6px 22px rgba(31, 42, 68, .045);
`;

const TableHeader = styled.div`
  padding: 20px 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  border-bottom: 1px solid #edf0f5;
  background: linear-gradient(180deg, #fff, #fcfcfe);

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 800;
    color: #252d3b;
    display: flex;
    align-items: center;
    gap: 11px;
  }
  h3 svg {
    width: 34px;
    height: 34px;
    padding: 9px;
    border-radius: 10px;
    color: #c70303;
    background: #fff0f0;
    font-size: 16px;
  }
  @media (max-width: 520px) { padding: 16px; }
`;

const SearchBox = styled.div`
  position: relative;
  width: 290px;
  max-width: 100%;
  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #8d96a7;
    font-size: 14px;
  }
  input {
    width: 100%;
    height: 42px;
    padding: 0 13px 0 40px;
    border: 1px solid #e2e7ef;
    border-radius: 11px;
    outline: none;
    background: #f9fafc;
    color: #303849;
    font-size: 12px;
    transition: .2s;
    &:focus {
      background: #fff;
      border-color: #c70303;
      box-shadow: 0 0 0 3px rgba(199,3,3,.08);
    }
    &::placeholder { color: #a4abba; }
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: visible;

  table {
    width: 100%;
    min-width: 790px;
    border-collapse: separate;
    border-spacing: 0;
  }
  thead { background: #f8f9fc; }
  th {
    padding: 15px 18px;
    color: #7b8495;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: .65px;
    text-transform: uppercase;
    text-align: left;
    border-bottom: 1px solid #edf0f5;
  }
  td {
    padding: 14px 18px;
    color: #424b5c;
    font-size: 12px;
    border-bottom: 1px solid #f0f2f6;
    transition: background .18s;
  }
  tbody tr:last-child td { border-bottom: 0; }
  tbody tr:hover td { background: #fffafa; }
  th, td { white-space: nowrap; }
  @media (max-width: 650px) { display: none; }
`;

const TeacherName = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  color: #252d3b;
  font-weight: 750;
  font-size: 12px;
  .avatar {
    width: 39px;
    height: 39px;
    flex-shrink: 0;
    border-radius: 12px;
    background: linear-gradient(145deg, #fff0f0, #ffe0e0);
    color: #bd0000;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    box-shadow: inset 0 0 0 1px rgba(199,3,3,.07);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  color: #697386;
  font-size: 11px;
  overflow-wrap: anywhere;
  svg {
    width: 27px;
    height: 27px;
    padding: 7px;
    border-radius: 8px;
    color: #65748b;
    background: #f0f3f8;
    font-size: 13px;
    flex-shrink: 0;
  }
`;

const Specialty = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 11px;
  border-radius: 9px;
  background: #eef4ff;
  color: #42669d;
  font-size: 10px;
  font-weight: 800;
  svg { font-size: 12px; }
`;

const DeleteButton = styled.button`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ffe0e0;
  border-radius: 10px;
  background: #fff5f5;
  color: #c70303;
  font-size: 13px;
  cursor: pointer;
  transition: .2s;
  &:hover {
    background: #c70303;
    border-color: #c70303;
    color: #fff;
    transform: translateY(-1px);
  }
  &:disabled { opacity: .5; cursor: not-allowed; transform: none; }
`;

const MobileList = styled.div`
  display: none;
  padding: 14px;
  @media (max-width: 650px) { display: grid; gap: 12px; }
`;

const MobileTeacher = styled.div`
  border: 1px solid #e9edf4;
  border-radius: 14px;
  padding: 15px;
  background: #fff;
  box-shadow: 0 3px 12px rgba(31,42,68,.035);
`;

const MobileTeacherTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f6;
`;

const MobileDetails = styled.div`
  display: grid;
  gap: 11px;
  color: #697386;
  font-size: 12px;
`;

const EmptyState = styled.div`
  padding: 55px 15px;
  text-align: center;
  color: #929bad;
  svg {
    display: block;
    margin: 0 auto 14px;
    width: 58px;
    height: 58px;
    padding: 16px;
    border-radius: 18px;
    color: #c70303;
    background: #fff0f0;
    font-size: 26px;
  }
  strong {
    display: block;
    color: #354052;
    font-size: 14px;
    margin-bottom: 6px;
  }
  p { margin: 0; font-size: 12px; }
`;

const LoadingState = styled.div`
  padding: 52px 20px;
  text-align: center;
  color: #7c8799;
  font-size: 12px;
  svg {
    display: block;
    animation: ${spin} 1s linear infinite;
    color: #c70303;
    font-size: 25px;
    margin: 0 auto 12px;
  }
`;

// ==========================================
// MODAL - SCROLL CORRIGIDO
// ==========================================

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 15px;
  background: rgba(17, 24, 39, .62);
  backdrop-filter: blur(7px);
  overflow: hidden;
  overscroll-behavior: contain;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 510px;
  max-height: calc(100dvh - 30px);

  overflow-y: auto;
  overscroll-behavior: contain;

  background: #fff;
  border: 1px solid rgba(255,255,255,.7);
  border-radius: 20px;
  box-shadow: 0 25px 80px rgba(15,23,42,.28);
  animation: ${slideUp} .22s ease-out;
`;

const ModalHeader = styled.div`
  padding: 22px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;

  position: sticky;
  top: 0;
  z-index: 2;
  background: white;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 850;
    color: #202633;
  }

  p {
    margin: 5px 0 0;
    font-size: 12px;
    color: #888;
  }
`;

const CloseButton = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 9px;
  background: #f5f5f5;
  color: #666;
  font-size: 15px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #fff0f0;
    color: #c70303;
  }

  &:disabled {
    opacity: .5;
    cursor: not-allowed;
  }
`;

const ModalBody = styled.div`
  padding: 23px 24px 25px;
`;

const Form = styled.form`
  display: grid;
  gap: 17px;
`;

const Field = styled.div`
  display: grid;
  gap: 7px;

  label {
    font-size: 11px;
    color: #465064;
    font-weight: 800;
    letter-spacing: .15px;
  }
`;

const InputWrapper = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 13px;
    top: 50%;
    transform: translateY(-50%);
    color: #c70303;
    font-size: 14px;
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 45px;
    border: 1px solid #e4e4e4;
    border-radius: 10px;
    padding: 0 13px 0 40px;
    outline: none;
    font-size: 13px;
    color: #333;
    transition: .2s;

    &:focus {
      border-color: #c70303;
      box-shadow: 0 0 0 3px rgba(199,3,3,.07);
    }

    &::placeholder {
      color: #b5b5b5;
    }
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 7px;

  @media (max-width: 420px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const CancelButton = styled.button`
  height: 43px;
  padding: 0 18px;
  border: 1px solid #e5e5e5;
  background: white;
  color: #666;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #f7f7f7;
  }

  &:disabled {
    opacity: .5;
    cursor: not-allowed;
  }
`;

const SaveButton = styled(AddButton)`
  height: 43px;
  min-width: 145px;
  justify-content: center;

  &:disabled {
    opacity: .65;
    cursor: not-allowed;
    transform: none;
  }
`;

// ==========================================
// COMPONENTE PROFESSORES
// ==========================================

function Professores() {
  const [professores, setProfessores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [pesquisa, setPesquisa] = useState("");

  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    especialidade: ""
  });

  // ========================================
  // CARREGAR PROFESSORES
  // ========================================

  const load = async () => {
    try {
      setLoading(true);

      const res = await api.get("/professores");

      setProfessores(
        Array.isArray(res.data) ? res.data : []
      );

    } catch (error) {
      console.error("Erro ao carregar professores:", error);

      toast.error(
        error.response?.data?.message ||
        "Não foi possível carregar os professores."
      );

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // ========================================
  // BLOQUEAR SCROLL DA PÁGINA DE FUNDO
  // ========================================

  useEffect(() => {
    if (modalAberto) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [modalAberto]);

  // ========================================
  // FECHAR MODAL COM ESC
  // ========================================

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && modalAberto && !saving) {
        setModalAberto(false);
      }
    };

    if (modalAberto) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalAberto, saving]);

  // ========================================
  // PESQUISA
  // ========================================

  const professoresFiltrados = useMemo(() => {
    const termo = pesquisa.trim().toLowerCase();

    if (!termo) return professores;

    return professores.filter((p) =>
      [
        p.nome,
        p.email,
        p.telefone,
        p.especialidade
      ].some((valor) =>
        String(valor || "").toLowerCase().includes(termo)
      )
    );
  }, [professores, pesquisa]);

  // ========================================
  // FORMULÁRIO
  // ========================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((anterior) => ({
      ...anterior,
      [name]: value
    }));
  };

  const limparFormulario = () => {
    setForm({
      nome: "",
      email: "",
      telefone: "",
      especialidade: ""
    });
  };

  const abrirModal = () => {
    limparFormulario();
    setModalAberto(true);
  };

  const fecharModal = () => {
    if (saving) return;

    setModalAberto(false);
    limparFormulario();
  };

  // ========================================
  // ADICIONAR PROFESSOR
  // ========================================

  const salvar = async (e) => {
    e.preventDefault();

    if (!form.nome.trim()) {
      toast.warning("Introduza o nome do professor.");
      return;
    }

    try {
      setSaving(true);

      await api.post("/professores", {
        nome: form.nome.trim(),
        email: form.email.trim() || null,
        telefone: form.telefone.trim() || null,
        especialidade: form.especialidade.trim() || null
      });

      toast.success("Professor adicionado com sucesso!");

      setModalAberto(false);
      limparFormulario();

      await load();

    } catch (error) {
      console.error("Erro ao adicionar professor:", error);

      toast.error(
        error.response?.data?.message ||
        "Não foi possível adicionar o professor."
      );

    } finally {
      setSaving(false);
    }
  };

  // ========================================
  // ELIMINAR PROFESSOR
  // ========================================

  const remover = async (id) => {
    const confirmar = window.confirm(
      "Tem a certeza de que pretende eliminar este professor?"
    );

    if (!confirmar) return;

    try {
      setDeleting(id);

      await api.delete(`/professores/${id}`);

      setProfessores((anterior) =>
        anterior.filter((p) => p.id !== id)
      );

      toast.success("Professor eliminado com sucesso!");

    } catch (error) {
      console.error("Erro ao eliminar professor:", error);

      toast.error(
        error.response?.data?.message ||
        "Não foi possível eliminar o professor."
      );

    } finally {
      setDeleting(null);
    }
  };

  // ========================================
  // INTERFACE
  // ========================================

  return (
    <Container>

      {/* CABEÇALHO */}

      <Header>
        <HeaderInfo>
          <HeaderIcon>
            <FaChalkboardTeacher />
          </HeaderIcon>

          <Heading>
            <h2>Professores</h2>
            <p>Gestão e organização do corpo docente</p>
          </Heading>
        </HeaderInfo>

        <AddButton onClick={abrirModal}>
          <FaPlus />
          Adicionar Professor
        </AddButton>
      </Header>

      {/* INDICADORES */}

      <StatsGrid>
        <StatCard>
          <StatIcon>
            <FaChalkboardTeacher />
          </StatIcon>

          <StatText>
            <span>Total de professores</span>
            <strong>{professores.length}</strong>
          </StatText>
        </StatCard>

        <StatCard>
          <StatIcon $bg="#eef4ff" $color="#3972c5">
            <FaUserGraduate />
          </StatIcon>

          <StatText>
            <span>Resultados encontrados</span>
            <strong>{professoresFiltrados.length}</strong>
          </StatText>
        </StatCard>

        <StatCard>
          <StatIcon $bg="#f2f0ff" $color="#7560bd">
            <FaGraduationCap />
          </StatIcon>

          <StatText>
            <span>Especialidades registadas</span>
            <strong>
              {
                new Set(
                  professores
                    .map(p => p.especialidade)
                    .filter(Boolean)
                ).size
              }
            </strong>
          </StatText>
        </StatCard>
      </StatsGrid>

      {/* LISTA DE PROFESSORES */}

      <ContentCard>
        <TableHeader>
          <h3>
            <FaUserTie />
            Lista de professores
          </h3>

          <SearchBox>
            <FaSearch />

            <input
              type="search"
              placeholder="Pesquisar professor..."
              value={pesquisa}
              onChange={(e) => setPesquisa(e.target.value)}
            />
          </SearchBox>
        </TableHeader>

        {loading ? (
          <LoadingState>
            <FaSpinner />
            <div>A carregar professores...</div>
          </LoadingState>

        ) : professoresFiltrados.length === 0 ? (
          <EmptyState>
            <FaChalkboardTeacher />

            <strong>
              {pesquisa
                ? "Nenhum professor encontrado"
                : "Ainda não existem professores"}
            </strong>

            <p>
              {pesquisa
                ? "Experimente pesquisar com outro termo."
                : "Clique em Adicionar Professor para começar."}
            </p>
          </EmptyState>

        ) : (
          <>
            {/* TABELA DESKTOP */}

            <TableWrapper>
              <table>
                <thead>
                  <tr>
                    <th>Professor</th>
                    <th>Email</th>
                    <th>Telefone</th>
                    <th>Especialidade</th>
                    <th style={{ textAlign: "center" }}>Acções</th>
                  </tr>
                </thead>

                <tbody>
                  {professoresFiltrados.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <TeacherName>
                          <div className="avatar">
                            <FaChalkboardTeacher />
                          </div>

                          {p.nome}
                        </TeacherName>
                      </td>

                      <td>
                        <ContactInfo>
                          <FaEnvelope />
                          {p.email || "Não informado"}
                        </ContactInfo>
                      </td>

                      <td>
                        <ContactInfo>
                          <FaPhone />
                          {p.telefone || "Não informado"}
                        </ContactInfo>
                      </td>

                      <td>
                        <Specialty>
                          <FaBook />
                          {p.especialidade || "Não definida"}
                        </Specialty>
                      </td>

                      <td style={{ textAlign: "center" }}>
                        <DeleteButton
                          title="Eliminar professor"
                          disabled={deleting === p.id}
                          onClick={() => remover(p.id)}
                        >
                          {deleting === p.id
                            ? <FaSpinner />
                            : <FaTrash />}
                        </DeleteButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrapper>

            {/* CARTÕES MOBILE */}

            <MobileList>
              {professoresFiltrados.map((p) => (
                <MobileTeacher key={p.id}>
                  <MobileTeacherTop>
                    <TeacherName>
                      <div className="avatar">
                        <FaChalkboardTeacher />
                      </div>

                      {p.nome}
                    </TeacherName>

                    <DeleteButton
                      title="Eliminar professor"
                      disabled={deleting === p.id}
                      onClick={() => remover(p.id)}
                    >
                      {deleting === p.id
                        ? <FaSpinner />
                        : <FaTrash />}
                    </DeleteButton>
                  </MobileTeacherTop>

                  <MobileDetails>
                    <ContactInfo>
                      <FaEnvelope />
                      {p.email || "Email não informado"}
                    </ContactInfo>

                    <ContactInfo>
                      <FaPhone />
                      {p.telefone || "Telefone não informado"}
                    </ContactInfo>

                    <ContactInfo>
                      <FaBook />
                      {p.especialidade || "Especialidade não definida"}
                    </ContactInfo>
                  </MobileDetails>
                </MobileTeacher>
              ))}
            </MobileList>
          </>
        )}
      </ContentCard>

      {/* MODAL */}

      {modalAberto && (
        <Overlay
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              fecharModal();
            }
          }}
        >
          <Modal>
            <ModalHeader>
              <div>
                <h3>Novo Professor</h3>
                <p>Preencha os dados do professor</p>
              </div>

              <CloseButton
                type="button"
                onClick={fecharModal}
                disabled={saving}
                title="Fechar"
              >
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <Form onSubmit={salvar}>

                <Field>
                  <label>Nome completo *</label>

                  <InputWrapper>
                    <FaUserTie />

                    <input
                      type="text"
                      name="nome"
                      placeholder="Nome completo do professor"
                      value={form.nome}
                      onChange={handleChange}
                      autoFocus
                      required
                    />
                  </InputWrapper>
                </Field>

                <Field>
                  <label>Email</label>

                  <InputWrapper>
                    <FaEnvelope />

                    <input
                      type="email"
                      name="email"
                      placeholder="exemplo@email.com"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </InputWrapper>
                </Field>

                <Field>
                  <label>Telefone</label>

                  <InputWrapper>
                    <FaPhone />

                    <input
                      type="tel"
                      name="telefone"
                      placeholder="Número de telefone"
                      value={form.telefone}
                      onChange={handleChange}
                    />
                  </InputWrapper>
                </Field>

                <Field>
                  <label>Especialidade</label>

                  <InputWrapper>
                    <FaBook />

                    <input
                      type="text"
                      name="especialidade"
                      placeholder="Ex.: Língua Inglesa"
                      value={form.especialidade}
                      onChange={handleChange}
                    />
                  </InputWrapper>
                </Field>

                <FormActions>
                  <CancelButton
                    type="button"
                    onClick={fecharModal}
                    disabled={saving}
                  >
                    Cancelar
                  </CancelButton>

                  <SaveButton
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <FaSpinner />
                        A guardar...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        Guardar Professor
                      </>
                    )}
                  </SaveButton>
                </FormActions>

              </Form>
            </ModalBody>
          </Modal>
        </Overlay>
      )}

    </Container>
  );
}

export default Professores;
